import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { USER_GENDER, GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import { getBirthday, getGender } from 'Src/helpers/accessor/accessor';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { getAllSuperAdminSchoolForms } from 'Src/helpers/service/superadmin/forms';
import { getAllSuperAdminSchoolHouses } from 'Src/helpers/service/superadmin/houses';
import {
  createSuperAdminSchoolStudent,
  deleteSuperAdminSchoolStudent,
  getAllSuperAdminSchoolStudents,
  getSuperAdminSchoolStudents,
  getSuperAdminSchoolStudentsCount,
  SuperAdminStudentData,
  SuperAdminStudentNextOfKin,
  updateSuperAdminSchoolStudent
} from 'Src/helpers/service/superadmin/students';
import './SuperAdminSchoolStudents.css';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['firstName']
  },
  {
    text: 'Surname',
    field: 'lastName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['lastName']
  },
  {
    text: 'Form',
    field: 'form.name',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name']
  },
  {
    text: 'House',
    field: 'house.name',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name']
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getGender
  },
  {
    text: 'Birthday',
    field: 'birthday',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getBirthday
  },
  {
    text: 'Email',
    field: 'email',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['email']
  },
  {
    text: 'MIS ID',
    field: 'misId',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['misId']
  },
  {
    text: 'MIS Email',
    field: 'misEmail',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['misEmail']
  }
];

interface Props {
  schoolId: string;
  history: History;
  location: Location;
}

interface StudentNextOfKinForm {
  relationship: string;
  firstName: string;
  lastName: string;
  email: string;
  phone1: string;
  phone2: string;
  phone3: string;
  priority: string;
}

interface StudentForm {
  firstName: string;
  lastName: string;
  birthday: string;
  gender: string;
  formId: string;
  houseId: string;
  medicalInfo: string;
  ethnicityCode: string;
  specialEducationNeeds: boolean;
  pupilsPremium: boolean;
  freeSchoolMeals: boolean;
  misId: string;
  misEmail: string;
  misPhone: string;
  nextOfKin: StudentNextOfKinForm[];
}

interface State {
  items: any[];
  currentPage: number;
  selectedItems: any[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  isSaving: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
  successMessage: string;
  forms: any[];
  houses: any[];
  isViewModalOpen: boolean;
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  form: StudentForm;
}

const EMPTY_STUDENT_FORM: StudentForm = {
  firstName: '',
  lastName: '',
  birthday: '',
  gender: USER_GENDER.MALE,
  formId: '',
  houseId: '',
  medicalInfo: '',
  ethnicityCode: '',
  specialEducationNeeds: false,
  pupilsPremium: false,
  freeSchoolMeals: false,
  misId: '',
  misEmail: '',
  misPhone: '',
  nextOfKin: []
};

function getEntityId(entity?: any, fallback?: string): string {
  return entity && (entity.id || entity._id) ? String(entity.id || entity._id) : fallback || '';
}

function getStudentId(student?: any): string {
  return getEntityId(student);
}

function getEntityName(entity?: any): string {
  if (!entity) {
    return '';
  }

  return entity.name || [entity.firstName, entity.lastName].filter(Boolean).join(' ') || getEntityId(entity);
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatBoolean(value?: boolean): string {
  return value ? 'Yes' : 'No';
}

function normalizeList(items: any[]): any[] {
  return (items || []).map(item => ({ ...item, id: item.id || item._id }));
}

function getFormId(student: any): string {
  return student.formId || getEntityId(student.form);
}

function getHouseId(student: any): string {
  return student.houseId || getEntityId(student.house);
}

function createEmptyNextOfKinForm(): StudentNextOfKinForm {
  return {
    relationship: '',
    firstName: '',
    lastName: '',
    email: '',
    phone1: '',
    phone2: '',
    phone3: '',
    priority: ''
  };
}

function nextOfKinToForm(kin: SuperAdminStudentNextOfKin): StudentNextOfKinForm {
  const phones = Array.isArray(kin.phones) ? kin.phones : [];

  return {
    relationship: kin.relationship || '',
    firstName: kin.firstName || '',
    lastName: kin.lastName || '',
    email: kin.email || '',
    phone1: phones[0] || '',
    phone2: phones[1] || '',
    phone3: phones[2] || '',
    priority: typeof kin.priority === 'number' ? String(kin.priority) : ''
  };
}

function nextOfKinFormToData(kin: StudentNextOfKinForm): SuperAdminStudentNextOfKin | undefined {
  const relationship = kin.relationship.trim();
  const firstName = kin.firstName.trim();
  const lastName = kin.lastName.trim();
  const email = kin.email.trim();
  const phones = [kin.phone1, kin.phone2, kin.phone3].map(phone => phone.trim()).filter(Boolean);
  const priority = kin.priority.trim();

  if (!relationship && !firstName && !lastName && !email && phones.length === 0 && !priority) {
    return undefined;
  }

  const data: SuperAdminStudentNextOfKin = {};

  if (relationship) {
    data.relationship = relationship;
  }

  if (firstName) {
    data.firstName = firstName;
  }

  if (lastName) {
    data.lastName = lastName;
  }

  if (email) {
    data.email = email;
  }

  if (phones.length > 0) {
    data.phones = phones;
  }

  if (priority) {
    data.priority = Number(priority);
  }

  return data;
}

function getNextOfKinValidationMessage(nextOfKin: StudentNextOfKinForm[]): string {
  for (let index = 0; index < nextOfKin.length; index++) {
    const kin = nextOfKin[index];
    const number = index + 1;
    const hasAnyValue = Boolean(
      kin.relationship.trim() ||
        kin.firstName.trim() ||
        kin.lastName.trim() ||
        kin.email.trim() ||
        kin.phone1.trim() ||
        kin.phone2.trim() ||
        kin.phone3.trim() ||
        kin.priority.trim()
    );

    if (!hasAnyValue) {
      continue;
    }

    if (kin.priority.trim() && isNaN(Number(kin.priority.trim()))) {
      return `Next of kin #${number}: priority should be a number.`;
    }

    if (kin.email.trim() && kin.email.indexOf('@') === -1) {
      return `Next of kin #${number}: email does not look valid.`;
    }
  }

  return '';
}

function studentToForm(student: any): StudentForm {
  const nextOfKin = Array.isArray(student.nextOfKin) ? student.nextOfKin : [];

  return {
    firstName: student.firstName || '',
    lastName: student.lastName || '',
    birthday: student.birthday ? String(student.birthday).slice(0, 10) : '',
    gender: student.gender || USER_GENDER.MALE,
    formId: getFormId(student),
    houseId: getHouseId(student),
    medicalInfo: student.medicalInfo || '',
    ethnicityCode: student.ethnicityCode || '',
    specialEducationNeeds: Boolean(student.specialEducationNeeds),
    pupilsPremium: Boolean(student.pupilsPremium),
    freeSchoolMeals: Boolean(student.freeSchoolMeals),
    misId: student.misId || '',
    misEmail: student.misEmail || '',
    misPhone: student.misPhone || '',
    nextOfKin: nextOfKin.map(nextOfKinToForm)
  };
}

function setIfString(target: any, field: string, value: string): void {
  const trimmedValue = value.trim();

  if (trimmedValue) {
    target[field] = trimmedValue;
  }
}

function buildStudentData(form: StudentForm): SuperAdminStudentData {
  const data: SuperAdminStudentData = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    gender: form.gender,
    specialEducationNeeds: form.specialEducationNeeds,
    pupilsPremium: form.pupilsPremium,
    freeSchoolMeals: form.freeSchoolMeals
  };

  setIfString(data, 'birthday', form.birthday);
  setIfString(data, 'formId', form.formId);
  setIfString(data, 'houseId', form.houseId);
  setIfString(data, 'medicalInfo', form.medicalInfo);
  setIfString(data, 'ethnicityCode', form.ethnicityCode);
  setIfString(data, 'misId', form.misId);
  setIfString(data, 'misEmail', form.misEmail);
  setIfString(data, 'misPhone', form.misPhone);

  const nextOfKin = form.nextOfKin.map(nextOfKinFormToData).filter(Boolean) as SuperAdminStudentNextOfKin[];

  data.nextOfKin = nextOfKin;

  return data;
}

function validateStudentData(data: SuperAdminStudentData): string {
  if (!data.firstName) {
    return 'Please enter first name.';
  }

  if (!data.lastName) {
    return 'Please enter last name.';
  }

  if (!data.gender) {
    return 'Please select gender.';
  }

  return '';
}

export class SuperAdminSchoolStudents extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      isSaving: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: '',
      successMessage: '',
      forms: [],
      houses: [],
      isViewModalOpen: false,
      isCreateModalOpen: false,
      isEditModalOpen: false,
      form: { ...EMPTY_STUDENT_FORM }
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.loadReferenceData();
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search || nextProps.schoolId !== this.props.schoolId) {
      this.setState({ isLoading: true, errorMessage: '', selectedItems: [] });
      this.loadReferenceData(nextProps.schoolId);
      this.setItems(nextProps);
    }
  }

  loadReferenceData = (schoolId: string = this.props.schoolId): void => {
    BPromise.all([getAllSuperAdminSchoolForms(schoolId), getAllSuperAdminSchoolHouses(schoolId)])
      .then(([forms, houses]) => {
        this.setState({
          forms: normalizeList(forms),
          houses: normalizeList(houses)
        });
      })
      .catch(error => {
        console.error('Can not load superadmin school student reference data', error);
      });
  };

  setItems(props: Props = this.props) {
    const { history, schoolId } = props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'lastName ASC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSuperAdminSchoolStudentsCount(schoolId, where),
      getSuperAdminSchoolStudents(schoolId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items: normalizeList(items),
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school students', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load students. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemId = getStudentId(selectedItem);
    const selectedItemIndex = selectedItems.findIndex(item => getStudentId(item) === selectedItemId);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated, successMessage: '' });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item =>
      selectedItems.every(selectedItem => getStudentId(selectedItem) !== getStudentId(item))
    );

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getServerFieldSectionWhere(filters);

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    getAllSuperAdminSchoolStudents(this.props.schoolId, where)
      .then(items => {
        const normalizedItems = normalizeList(items);
        const itemsFiltered = normalizedItems.filter(item =>
          selectedItems.every(selectedItem => getStudentId(selectedItem) !== getStudentId(item))
        );

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin school students', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all students. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter ? filter.type : FILTER_TYPE.TEXT;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];

        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }

        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter
    });
  };

  onCreateStudentClick = (): void => {
    this.setState({
      isCreateModalOpen: true,
      isEditModalOpen: false,
      isViewModalOpen: false,
      form: { ...EMPTY_STUDENT_FORM },
      errorMessage: '',
      successMessage: ''
    });
  };

  onViewStudentClick = (): void => {
    if (!this.state.selectedItems[0]) {
      return;
    }

    this.setState({
      isViewModalOpen: true,
      isCreateModalOpen: false,
      isEditModalOpen: false,
      errorMessage: '',
      successMessage: ''
    });
  };

  onEditStudentClick = (): void => {
    const selectedStudent = this.state.selectedItems[0];

    if (!selectedStudent) {
      return;
    }

    this.setState({
      isCreateModalOpen: false,
      isEditModalOpen: true,
      isViewModalOpen: false,
      form: studentToForm(selectedStudent),
      errorMessage: '',
      successMessage: ''
    });
  };

  onDeleteStudentsClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length === 0) {
      return;
    }

    const shouldDelete = window.confirm(
      selectedItems.length === 1
        ? 'Delete selected student? This can affect linked permissions and event participation.'
        : `Delete ${selectedItems.length} selected students? This can affect linked permissions and event participation.`
    );

    if (!shouldDelete) {
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    BPromise.all(
      selectedItems.map(student => deleteSuperAdminSchoolStudent(this.props.schoolId, getStudentId(student)))
    )
      .then(() => {
        this.setState(
          {
            isSaving: false,
            selectedItems: [],
            isSelectAllChecked: false,
            successMessage: selectedItems.length === 1 ? 'Student has been deleted.' : 'Students have been deleted.'
          },
          () => this.setItems(this.props)
        );
      })
      .catch(error => {
        console.error('Can not delete superadmin school students', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not delete selected students. Please try again.'
        });
      });
  };

  onClosePanelClick = (): void => {
    this.setState({
      isViewModalOpen: false,
      isCreateModalOpen: false,
      isEditModalOpen: false,
      form: { ...EMPTY_STUDENT_FORM },
      errorMessage: ''
    });
  };

  onFormChange = (event): void => {
    const { name, value, type, checked } = event.target;

    this.setState({
      form: {
        ...this.state.form,
        [name]: type === 'checkbox' ? checked : value
      }
    } as any);
  };

  onNextOfKinChange = (index: number, field: keyof StudentNextOfKinForm, value: string): void => {
    const nextOfKin = this.state.form.nextOfKin.slice();
    const item = nextOfKin[index] || createEmptyNextOfKinForm();

    nextOfKin[index] = {
      ...item,
      [field]: value
    };

    this.setState({
      form: {
        ...this.state.form,
        nextOfKin
      }
    });
  };

  onAddNextOfKinClick = (): void => {
    this.setState({
      form: {
        ...this.state.form,
        nextOfKin: [...this.state.form.nextOfKin, createEmptyNextOfKinForm()]
      }
    });
  };

  onRemoveNextOfKinClick = (index: number): void => {
    const nextOfKin = this.state.form.nextOfKin.slice();
    nextOfKin.splice(index, 1);

    this.setState({
      form: {
        ...this.state.form,
        nextOfKin
      }
    });
  };

  onSaveCreateStudentClick = (): void => {
    const nextOfKinValidationMessage = getNextOfKinValidationMessage(this.state.form.nextOfKin);

    if (nextOfKinValidationMessage) {
      this.setState({ errorMessage: nextOfKinValidationMessage });
      return;
    }

    const data = buildStudentData(this.state.form);
    const validationMessage = validateStudentData(data);

    if (validationMessage) {
      this.setState({ errorMessage: validationMessage });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    createSuperAdminSchoolStudent(this.props.schoolId, data)
      .then(() => {
        this.setState(
          {
            isSaving: false,
            isCreateModalOpen: false,
            form: { ...EMPTY_STUDENT_FORM },
            successMessage: 'Student has been created.'
          },
          () => this.setItems(this.props)
        );
      })
      .catch(error => {
        console.error('Can not create superadmin school student', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not create student. Please check fields and try again.'
        });
      });
  };

  onSaveEditStudentClick = (): void => {
    const selectedStudent = this.state.selectedItems[0];
    const studentId = getStudentId(selectedStudent);

    if (!studentId) {
      return;
    }

    const nextOfKinValidationMessage = getNextOfKinValidationMessage(this.state.form.nextOfKin);

    if (nextOfKinValidationMessage) {
      this.setState({ errorMessage: nextOfKinValidationMessage });
      return;
    }

    const data = buildStudentData(this.state.form);
    const validationMessage = validateStudentData(data);

    if (validationMessage) {
      this.setState({ errorMessage: validationMessage });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    updateSuperAdminSchoolStudent(this.props.schoolId, studentId, data)
      .then(() => {
        this.setState(
          {
            isSaving: false,
            isEditModalOpen: false,
            selectedItems: [],
            form: { ...EMPTY_STUDENT_FORM },
            successMessage: 'Student has been updated.'
          },
          () => this.setItems(this.props)
        );
      })
      .catch(error => {
        console.error('Can not update superadmin school student', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not update student. Please check fields and try again.'
        });
      });
  };

  renderSelectOptions(items: any[], emptyText: string): React.ReactNode {
    return [
      <option key="empty" value="">
        {emptyText}
      </option>,
      ...items.map(item => (
        <option key={getEntityId(item)} value={getEntityId(item)}>
          {getEntityName(item)}
        </option>
      ))
    ];
  }

  renderNextOfKinForm(): React.ReactNode {
    const { form, isSaving } = this.state;

    return (
      <div className="SuperAdminSchoolStudentsNextOfKinForm">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="mb-0">Next of kin</label>
          <button
            className="btn btn-sm btn-outline-primary"
            type="button"
            onClick={this.onAddNextOfKinClick}
            disabled={isSaving}
          >
            Add contact
          </button>
        </div>

        {form.nextOfKin.length === 0 && <div className="text-muted mb-2">No next of kin contacts added.</div>}

        {form.nextOfKin.map((kin, index) => (
          <div className="SuperAdminSchoolStudentsNextOfKinFormCard" key={`next_of_kin_form_${index}`}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Contact #{index + 1}</strong>
              <button
                className="btn btn-sm btn-outline-danger"
                type="button"
                onClick={() => this.onRemoveNextOfKinClick(index)}
                disabled={isSaving}
              >
                Remove
              </button>
            </div>

            <div className="form-row">
              <div className="form-group col-md-3">
                <label>Relationship</label>
                <input
                  className="form-control"
                  value={kin.relationship}
                  onChange={event => this.onNextOfKinChange(index, 'relationship', event.target.value)}
                  disabled={isSaving}
                  placeholder="Mother"
                />
              </div>
              <div className="form-group col-md-3">
                <label>First name</label>
                <input
                  className="form-control"
                  value={kin.firstName}
                  onChange={event => this.onNextOfKinChange(index, 'firstName', event.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="form-group col-md-3">
                <label>Last name</label>
                <input
                  className="form-control"
                  value={kin.lastName}
                  onChange={event => this.onNextOfKinChange(index, 'lastName', event.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="form-group col-md-3">
                <label>Email</label>
                <input
                  className="form-control"
                  value={kin.email}
                  onChange={event => this.onNextOfKinChange(index, 'email', event.target.value)}
                  disabled={isSaving}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-md-3">
                <label>Phone 1</label>
                <input
                  className="form-control"
                  value={kin.phone1}
                  onChange={event => this.onNextOfKinChange(index, 'phone1', event.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="form-group col-md-3">
                <label>Phone 2</label>
                <input
                  className="form-control"
                  value={kin.phone2}
                  onChange={event => this.onNextOfKinChange(index, 'phone2', event.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="form-group col-md-3">
                <label>Phone 3</label>
                <input
                  className="form-control"
                  value={kin.phone3}
                  onChange={event => this.onNextOfKinChange(index, 'phone3', event.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="form-group col-md-3">
                <label>Priority</label>
                <input
                  className="form-control"
                  type="number"
                  value={kin.priority}
                  onChange={event => this.onNextOfKinChange(index, 'priority', event.target.value)}
                  disabled={isSaving}
                  placeholder="1"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderStudentForm(isEdit: boolean): React.ReactNode {
    const { form, forms, houses, isSaving } = this.state;

    return (
      <div className="card mt-3 mb-3 SuperAdminSchoolStudentsPanel">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{isEdit ? 'Edit student' : 'Add student'}</strong>
          <button
            className="btn btn-sm btn-outline-secondary"
            type="button"
            onClick={this.onClosePanelClick}
            disabled={isSaving}
          >
            Close
          </button>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group col-md-4">
              <label>First name</label>
              <input
                className="form-control"
                name="firstName"
                value={form.firstName}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-4">
              <label>Last name</label>
              <input
                className="form-control"
                name="lastName"
                value={form.lastName}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-4">
              <label>Gender</label>
              <select
                className="form-control"
                name="gender"
                value={form.gender}
                onChange={this.onFormChange}
                disabled={isSaving}
              >
                <option value={USER_GENDER.MALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.MALE || 'Male'}</option>
                <option value={USER_GENDER.FEMALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE || 'Female'}</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-4">
              <label>Birthday</label>
              <input
                className="form-control"
                name="birthday"
                type="date"
                value={form.birthday}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-4">
              <label>Form</label>
              <select
                className="form-control"
                name="formId"
                value={form.formId}
                onChange={this.onFormChange}
                disabled={isSaving}
              >
                {this.renderSelectOptions(forms, 'No form')}
              </select>
            </div>
            <div className="form-group col-md-4">
              <label>House</label>
              <select
                className="form-control"
                name="houseId"
                value={form.houseId}
                onChange={this.onFormChange}
                disabled={isSaving}
              >
                {this.renderSelectOptions(houses, 'No house')}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-4">
              <label>MIS ID</label>
              <input
                className="form-control"
                name="misId"
                value={form.misId}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-4">
              <label>MIS Email</label>
              <input
                className="form-control"
                name="misEmail"
                value={form.misEmail}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-4">
              <label>MIS Phone</label>
              <input
                className="form-control"
                name="misPhone"
                value={form.misPhone}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-12">
              <label>Medical info</label>
              <textarea
                className="form-control"
                name="medicalInfo"
                rows={3}
                value={form.medicalInfo}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
          </div>

          {this.renderNextOfKinForm()}

          <div className="form-row">
            <div className="form-group col-md-4">
              <label>Ethnicity code</label>
              <input
                className="form-control"
                name="ethnicityCode"
                value={form.ethnicityCode}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-8 d-flex align-items-end SuperAdminSchoolStudentsFlags">
              <label className="mr-3">
                <input
                  type="checkbox"
                  name="specialEducationNeeds"
                  checked={form.specialEducationNeeds}
                  onChange={this.onFormChange}
                  disabled={isSaving}
                />{' '}
                SEN
              </label>
              <label className="mr-3">
                <input
                  type="checkbox"
                  name="pupilsPremium"
                  checked={form.pupilsPremium}
                  onChange={this.onFormChange}
                  disabled={isSaving}
                />{' '}
                Pupils premium
              </label>
              <label className="mr-3">
                <input
                  type="checkbox"
                  name="freeSchoolMeals"
                  checked={form.freeSchoolMeals}
                  onChange={this.onFormChange}
                  disabled={isSaving}
                />{' '}
                Free school meals
              </label>
            </div>
          </div>

          <button
            className="btn btn-primary mr-2"
            type="button"
            onClick={isEdit ? this.onSaveEditStudentClick : this.onSaveCreateStudentClick}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={this.onClosePanelClick}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  renderDetailRow(label: string, value: React.ReactNode): React.ReactNode {
    return (
      <div className="SuperAdminSchoolStudentsDetailRow">
        <div className="SuperAdminSchoolStudentsDetailLabel">{label}</div>
        <div className="SuperAdminSchoolStudentsDetailValue">{value || '—'}</div>
      </div>
    );
  }

  renderNextOfKin(student: any): React.ReactNode {
    const nextOfKin = Array.isArray(student.nextOfKin) ? student.nextOfKin : [];

    if (nextOfKin.length === 0) {
      return <div className="text-muted">No next of kin details.</div>;
    }

    return (
      <div className="SuperAdminSchoolStudentsKinList">
        {nextOfKin.map((kin, index) => (
          <div className="SuperAdminSchoolStudentsKinCard" key={`kin_${index}`}>
            <strong>{[kin.firstName, kin.lastName].filter(Boolean).join(' ') || 'Contact'}</strong>
            <div className="text-muted">{kin.relationship || 'No relationship'}</div>
            <div>Email: {kin.email || '—'}</div>
            <div>Phones: {Array.isArray(kin.phones) && kin.phones.length > 0 ? kin.phones.join(', ') : '—'}</div>
            <div>Priority: {typeof kin.priority === 'number' ? kin.priority : '—'}</div>
          </div>
        ))}
      </div>
    );
  }

  renderStudentDetails(): React.ReactNode {
    const student = this.state.selectedItems[0];

    if (!student) {
      return null;
    }

    return (
      <div className="card mt-3 mb-3 SuperAdminSchoolStudentsPanel">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Student details</strong>
          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={this.onClosePanelClick}>
            Close
          </button>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              {this.renderDetailRow('Name', [student.firstName, student.lastName].filter(Boolean).join(' '))}
              {this.renderDetailRow('Gender', getGender(student))}
              {this.renderDetailRow('Birthday', getBirthday(student))}
              {this.renderDetailRow('Form', getEntityName(student.form))}
              {this.renderDetailRow('House', getEntityName(student.house))}
              {this.renderDetailRow('Email', student.email)}
            </div>
            <div className="col-md-6">
              {this.renderDetailRow('MIS ID', student.misId)}
              {this.renderDetailRow('MIS Email', student.misEmail)}
              {this.renderDetailRow('MIS Phone', student.misPhone)}
              {this.renderDetailRow('SEN', formatBoolean(Boolean(student.specialEducationNeeds)))}
              {this.renderDetailRow('Pupils premium', formatBoolean(Boolean(student.pupilsPremium)))}
              {this.renderDetailRow('Free school meals', formatBoolean(Boolean(student.freeSchoolMeals)))}
              {this.renderDetailRow('Created', formatDateTime(student.createdAt))}
              {this.renderDetailRow('Updated', formatDateTime(student.updatedAt))}
            </div>
          </div>

          <div className="mt-3">
            <h6>Medical info</h6>
            <div className="SuperAdminSchoolStudentsTextBox">{student.medicalInfo || '—'}</div>
          </div>

          <div className="mt-3">
            <h6>Next of kin</h6>
            {this.renderNextOfKin(student)}
          </div>

          <details className="mt-3">
            <summary>Raw student JSON</summary>
            <pre className="SuperAdminSchoolStudentsJson">{JSON.stringify(student, null, 2)}</pre>
          </details>
        </div>
      </div>
    );
  }

  render() {
    const {
      currentPage,
      errorMessage,
      successMessage,
      filters,
      isDataFiltered,
      isLoading,
      isSaving,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total,
      isViewModalOpen,
      isCreateModalOpen,
      isEditModalOpen
    } = this.state;

    const hasOneSelectedItem = selectedItems.length === 1;
    const hasSelectedItems = selectedItems.length > 0;
    const actionItems: ActionItem[] = [
      {
        itemText: 'Add student',
        isActive: true,
        onItemClick: this.onCreateStudentClick
      },
      {
        itemText: 'View selected student',
        isActive: hasOneSelectedItem,
        onItemClick: this.onViewStudentClick
      },
      {
        itemText: 'Edit selected student',
        isActive: hasOneSelectedItem,
        onItemClick: this.onEditStudentClick
      },
      {
        itemText: selectedItems.length > 1 ? 'Delete selected students' : 'Delete selected student',
        isActive: hasSelectedItems,
        onItemClick: this.onDeleteStudentsClick
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="SuperAdminSchoolStudents">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Students</h4>
            <div className="text-muted">View, add and update students for this school.</div>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => this.setItems()} disabled={isSaving}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && !isLoading && <div className="alert alert-success">{successMessage}</div>}
        {isSaving && <Loader />}

        {isViewModalOpen && this.renderStudentDetails()}
        {isCreateModalOpen && this.renderStudentForm(false)}
        {isEditModalOpen && this.renderStudentForm(true)}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />
      </div>
    );
  }
}

